import { useCallback, useRef } from 'react';

/**
 * Custom hook for debouncing function calls
 * @param {Function} callback - Function to debounce
 * @param {number} delay - Delay in milliseconds (default: 500ms)
 * @returns {Function} Debounced function
 */
export const useDebounce = (callback, delay = 500) => {
  const debounceTimeoutRef = useRef(null);

  return useCallback((...args) => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

/**
 * Custom hook for debouncing search input with value management
 * @param {Function} onSearch - Search function to call
 * @param {number} delay - Delay in milliseconds (default: 500ms)
 * @returns {Object} Object with debouncedSearch function and cleanup
 */
export const useSearchDebounce = (onSearch, delay = 500) => {
  const debounceTimeoutRef = useRef(null);

  const debouncedSearch = useCallback((searchTerm) => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      onSearch(searchTerm);
    }, delay);
  }, [onSearch, delay]);

  const cleanup = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
  }, []);

  return { debouncedSearch, cleanup };
};

export default useDebounce;
